import { useState } from "react";
import { Camera, Trash2, Loader2, Save, Plus, X, ImageIcon } from "lucide-react";
import { PLH_IMG_ALT, PLH_PLUS, TIP_REMOVE_IMAGE, TIP_REMOVE_INDICATOR } from "../../lib/constants";
import { uploadSiteImage } from "../../lib/api";
import { toast } from "sonner";
import { TOAST_IMAGE_UPLOADED, TOAST_IMAGE_ERROR } from "../../lib/constants";

export default function AdminAboutEditor({ readOnly = false, about, setAbout, uploading, handleImageUpload, handleRemoveImage, saving, handleSaveAbout }) {
  const [logoUploading, setLogoUploading] = useState({});

  const partnerLogos = Array.isArray(about?.partner_logos) ? about.partner_logos : typeof about?.partner_logos === "string" ? (() => { try { return JSON.parse(about.partner_logos); } catch { return []; } })() : [];

  async function handleLogoUpload(e, idx) {
    const file = e.target.files?.[0]; if (!file) return;
    setLogoUploading(prev => ({ ...prev, [idx]: true }));
    try {
      const url = await uploadSiteImage(file);
      const logos = [...partnerLogos]; logos[idx] = url;
      setAbout({ ...about, partner_logos: logos });
      toast.success(TOAST_IMAGE_UPLOADED);
    } catch (err) { toast.error(TOAST_IMAGE_ERROR); } finally { setLogoUploading(prev => ({ ...prev, [idx]: false })); }
  }

  function handleLogoRemove(idx) {
    const logos = partnerLogos.filter((_, i) => i !== idx);
    setAbout({ ...about, partner_logos: logos });
  }

  function addLogoSlot() {
    setAbout({ ...about, partner_logos: [...partnerLogos, ""] });
  }

  const paragraphs = Array.isArray(about?.paragraphs)
    ? about.paragraphs
    : typeof about?.paragraphs === "string"
      ? JSON.parse(about.paragraphs || "[]")
      : [about?.description_1 || "", about?.description_2 || ""].filter(Boolean);

  const stats = Array.isArray(about?.stats)
    ? about.stats
    : typeof about?.stats === "string"
      ? JSON.parse(about.stats || "[]")
      : [];

  return (
    <div data-id="admin-about" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
      <fieldset disabled={readOnly}>
        <div className="space-y-12">
          <div className="grid xl:grid-cols-12 gap-10">
            <div className="xl:col-span-4">
              <div className="sticky top-24">
                <p className="eyebrow mb-4 text-champagne">Retrato Editorial</p>
                <div className="relative group aspect-[3/4] bg-fendi/5 overflow-hidden shadow-sm">
                  {about?.image ? (
                    <img src={about.image} alt="About" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-warmgray/30 gap-3">
                      <Camera size={40} strokeWidth={1} />
                      <span className="text-[10px] uppercase tracking-widest">Sem imagem</span>
                    </div>
                  )}
                  <label className={`absolute inset-0 bg-softblack/0 ${readOnly ? '' : 'group-hover:bg-softblack/50 cursor-pointer'} transition-all flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white`}>
                    {!readOnly && about?.image && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveImage("about"); }}
                        className="absolute top-3 right-3 bg-white/10 hover:bg-destructive p-1.5 rounded-full transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {!readOnly && (uploading.about ? <Loader2 className="animate-spin" /> : <><Camera size={22} className="mb-1.5" /><span className="text-[9px] uppercase tracking-widest">Trocar</span></>)}
                    {!readOnly && <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, "about")} disabled={uploading.about} />}
                  </label>
                </div>
                <label className="block mt-4">
                  <span className="eyebrow text-[10px] text-warmgray">Alt da imagem (SEO)</span>
                  <input className="editorial-field mt-2 !text-xs" value={about?.img_alt || ""}
                    onChange={e => setAbout({...about, img_alt: e.target.value})} placeholder={PLH_IMG_ALT} />
                </label>
              </div>
            </div>

            <div className="xl:col-span-8 space-y-10">
              <label className="block mb-6">
                <span className="eyebrow text-[10px] text-warmgray">Eyebrow</span>
                <input className="editorial-field mt-2 !text-[10px] tracking-[0.2em]" value={about?.eyebrow || ""}
                  onChange={e => setAbout({...about, eyebrow: e.target.value})} />
              </label>
              <label className="block mb-6">
                <span className="eyebrow text-[10px] text-warmgray">Título</span>
                <input className="editorial-field mt-2 !text-2xl font-serif" value={about?.headline || ""}      
                  onChange={e => setAbout({...about, headline: e.target.value})} />
              </label>

              <div className="h-px bg-fendi/20" />

              <div>
                <p className="eyebrow text-champagne mb-4">Parágrafos</p>
                <p className="text-[10px] text-warmgray/60 mb-3">Separe cada parágrafo com uma linha em branco (Enter duplo).</p>
                <textarea
                  className="editorial-field text-sm font-light !leading-relaxed w-full"
                  rows={12}
                  value={paragraphs.join("\n\n")}
                  onChange={e => setAbout({ ...about, paragraphs: e.target.value.split("\n\n") })}
                  placeholder="Escreva os parágrafos da seção Sobre..."
                />
              </div>

              <div className="h-px bg-fendi/20" />

              <div>
                <div className="flex items-center justify-between mb-6">
                  <p className="eyebrow text-champagne">Indicadores</p>
                  {!readOnly && (
                    <button type="button" onClick={() => setAbout({ ...about, stats: [...stats, { n: "", label: "", prefix: "" }] })}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-champagne hover:text-softblack transition-colors">
                      <Plus size={12} /> Adicionar
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {stats.map((stat, idx) => (
                    <div key={idx} className="relative group/stat">
                      {!readOnly && (
                        <button onClick={() => setAbout({...about, stats: stats.filter((_, i) => i !== idx)})}
                          className="absolute -top-2 -right-2 p-1 rounded-full bg-white border border-red-200 text-red-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover/stat:opacity-100 transition-all z-10 shadow-sm"
                          title={TIP_REMOVE_INDICATOR}>
                          <Trash2 size={12} />
                        </button>
                      )}
                      <div className="flex items-end gap-2 mb-2">
                        <input className="bg-transparent border-b border-fendi/20 focus:border-champagne outline-none !text-xs w-8 text-center text-warmgray pb-1 shrink-0"
                          placeholder={PLH_PLUS}
                          value={stat.prefix || ""}
                          onChange={e => {
                            const newStats = [...stats];
                            newStats[idx].prefix = e.target.value;
                            setAbout({...about, stats: newStats});
                          }} />
                        <input className="bg-transparent border-none focus:ring-0 outline-none !text-3xl font-serif text-softblack pb-1 flex-1 min-w-0 transition-colors"
                          value={stat.n}
                          onChange={e => {
                            const newStats = [...stats];
                            newStats[idx].n = e.target.value;
                            setAbout({...about, stats: newStats});
                          }} />
                      </div>
                      <input className="bg-transparent border-none focus:ring-0 outline-none text-[10px] uppercase tracking-[0.2em] text-warmgray w-full truncate"
                        value={stat.label}
                        onChange={e => {
                          const newStats = [...about.stats];
                          newStats[idx].label = e.target.value;
                          setAbout({...about, stats: newStats});
                        }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-fendi/20" />

          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="eyebrow text-champagne">Logos de Associados</p>
              {!readOnly && (
                <button type="button" onClick={addLogoSlot} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-champagne hover:text-softblack transition-colors">
                  <Plus size={12} /> Adicionar Logo
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {partnerLogos.map((url, idx) => (
                <div key={idx} className="relative group aspect-[3/2] bg-fendi/5 overflow-hidden border border-fendi/20 rounded-lg flex items-center justify-center">
                  {url ? (
                    <>
                      <img src={url} alt={`Logo ${idx + 1}`} className="w-full h-full object-contain p-4" />
                      {!readOnly && (
                        <button onClick={() => handleLogoRemove(idx)} className="absolute top-1.5 right-1.5 p-1 bg-white/90 rounded-full shadow hover:bg-white transition-colors opacity-0 group-hover:opacity-100">
                          <X size={10} className="text-red-500" />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-1.5 text-warmgray/30">
                      <ImageIcon size={22} strokeWidth={1} />
                      <span className="text-[8px] uppercase tracking-widest">Logo {idx + 1}</span>
                    </div>
                  )}
                  {!readOnly && (
                    <label className={`absolute inset-0 bg-softblack/0 group-hover:bg-softblack/40 cursor-pointer transition-all flex items-center justify-center opacity-0 group-hover:opacity-100`}>
                      {logoUploading[idx] ? (
                        <Loader2 size={18} className="animate-spin text-white" />
                      ) : (
                        <Camera size={18} className="text-white" />
                      )}
                      <input type="file" className="hidden" accept="image/*" onChange={e => handleLogoUpload(e, idx)} disabled={logoUploading[idx]} />
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end pt-6 border-t border-fendi/10">
              <button onClick={handleSaveAbout} disabled={saving} className="btn-primary !px-10 !py-4 shadow-lg shadow-champagne/10">
                <Save size={16} className="mr-3" /> {saving ? "A guardar..." : "Publicar Alterações"}
              </button>
            </div>
          )}
        </div>
      </fieldset>
    </div>
  );
}
